import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import { router } from '@/core/router';
import { hardRedirect } from '@/core/navigation/redirect.service';
import breakpointsState from '@/core/responsive/breakpoints/breakpoints-state.observable';
import siteContextStore from '@/shared/content/site-context.store';
import { NavigationItemModel, NavigationType } from '@/types/B2CcontentServerContract';

class NavigationStore {
    private state: INavigationState = reactive({
        mainNavigation: siteContextStore.navigation,
        hasActiveSearch: false,
        hasActiveBasket: false,
        activeMainNavigationItem: null,
        activeSubNavigationItem: null,
    });

    public constructor() {
        makeStoreDebugAccessible('navigationStore', this.state);
    }

    public setActiveMainNavigationItem(item: NavigationItemModel | null): void {
        this.state.activeMainNavigationItem = item;
    }

    public setActiveSubNavigationItem(item: NavigationItemModel | null): void {
        this.state.activeSubNavigationItem = item;
    }

    public clearActiveMainNavigation(): void {
        this.setActiveSearch(false);
        this.setActiveBasket(false);
        this.setActiveMainNavigationItem(null);
        this.setActiveSubNavigationItem(null);
    }

    public toggleActiveMainNavigationItem(item: NavigationItemModel | null): void {
        if (item === this.activeMainNavigationItem) {
            item = null;
        }
        this.clearActiveMainNavigation();
        this.setActiveMainNavigationItem(item);
    }

    public toggleActiveSubNavigationItem(item: NavigationItemModel | null): void {
        if (item === this.activeSubNavigationItem) {
            this.setActiveSubNavigationItem(null);
        } else {
            this.setActiveSubNavigationItem(item);
        }
    }

    public handleMainNavigationClick(item: NavigationItemModel): void {
        if (item.items && item.items.length > 0) {
            this.toggleActiveMainNavigationItem(item);
        } else if (item.url) {
            this.navigateToItem(item);
        }
    }

    public handleSubNavigationClick(item: NavigationItemModel): void {
        if (item.items && item.items.length > 0) {
            this.toggleActiveSubNavigationItem(item);
        } else if (item.url) {
            this.navigateToItem(item);
        }
    }

    private navigateToItem(item: NavigationItemModel): void {
        if (item.spaLink) {
            router.push(item.url);
        } else if (item.target === '_blank') {
            window.open(item.url, item.target);
        } else {
            hardRedirect(item.url);
        }
        this.clearActiveMainNavigation();
    }

    public get activeMainNavigationItem(): NavigationItemModel | null {
        return this.state.activeMainNavigationItem;
    }

    public get activeSubNavigationItem(): NavigationItemModel | null {
        return this.state.activeSubNavigationItem;
    }

    public get activeMainNavigationItems(): NavigationItemModel[] {
        return this.activeMainNavigationItem?.items ?? [] as NavigationItemModel[];
    }

    public get activeMainNavigationComponent(): 'SearchArea' | 'MiniBasketArea' | 'MainNavigation' | 'MainNavigationMobile' {
        if (this.isMobileNavigation) {
            return this.hasActiveSearch ? 'SearchArea' : 'MainNavigationMobile';
        } else {
            return this.hasActiveSearch
                ? 'SearchArea' : this.hasActiveBasket
                    ? 'MiniBasketArea' : 'MainNavigation';
        }
    }

    public get activeMainNavigationIdentifier(): string {
        const fallbackIdentifier = this.hasActiveSearch
            ? 'Search' : this.hasActiveBasket
                ? 'Basket' : '';
        const identifier = this.activeMainNavigationItem?.url ?? '' + this.activeMainNavigationItem?.name ?? '';
        return identifier !== '' ? identifier : fallbackIdentifier;
    }

    public get mainNavigation(): NavigationItemModel[] {
        return this.state.mainNavigation;
    }

    private getMainNavigationByType(type: NavigationType): NavigationItemModel[] {
        return this.mainNavigation.filter((item) => item.type === type);
    }

    public get mainLeftNavigation(): NavigationItemModel[] {
        return this.getMainNavigationByType(NavigationType.Left);
    }

    public get mainRightNavigation(): NavigationItemModel[] {
        return this.getMainNavigationByType(NavigationType.Right);
    }

    public get myBoliaNavigation(): NavigationItemModel[] {
        return this.getMainNavigationByType(NavigationType.MyBolia);
    }

    public toggleActiveSearch(): void {
        this.state.hasActiveSearch = !this.state.hasActiveSearch;
        this.setActiveBasket(false);
        this.setActiveMainNavigationItem(null);
    }

    public toggleActiveBasket(): void {
        this.state.hasActiveBasket = !this.state.hasActiveBasket;
        this.setActiveSearch(false);
        this.setActiveMainNavigationItem(null);
    }

    public setActiveSearch(isActive: boolean): void {
        this.state.hasActiveSearch = isActive;
    }

    public setActiveBasket(isActive: boolean): void {
        this.state.hasActiveBasket = isActive;
    }

    public getIsMyBoliaItem = (item: NavigationItemModel) => {
        return item?.type === NavigationType.MyBolia ?? false;
    };

    public get hasActiveSearch(): boolean {
        return this.state.hasActiveSearch;
    }

    public get hasActiveBasket(): boolean {
        return this.state.hasActiveBasket;
    }

    public get isMobileNavigation(): boolean {
        return breakpointsState.isBreakpointActive('max-md');
    }
}

export interface INavigationState {
    hasActiveSearch: boolean;
    hasActiveBasket: boolean;
    mainNavigation: NavigationItemModel[];
    activeMainNavigationItem: NavigationItemModel | null;
    activeSubNavigationItem: NavigationItemModel | null;
}

export default new NavigationStore();
